/**
 * 마켓 랭킹이 쓰는 **사람이 읽는 이름과 색**.
 *
 * 경계(`types/product-rankings`)가 나르는 것은 코드값(`oliveyoung` · `exact` · `ranking`)뿐이다.
 * 사람이 읽는 이름은 색·순서와 함께 **화면이 쥔다** — 서버 응답에 라벨을 실어 보내지 않는다.
 */

import {
  isPriceInBand,
  PRODUCT_PRICE_BAND_SPECS,
  formatProductPriceBandLabel,
} from 'src/constants/product-price-bands';
import { PRODUCT_KIND_OPTIONS, PRODUCT_REGION_OPTIONS, PRODUCT_SEARCH_SORT_OPTIONS } from 'src/constants/product-search-options';
import {
  PRODUCT_SOURCE_ORDER,
  PRODUCT_SOURCE_LABELS,
  OVERSEAS_PRODUCT_SOURCE_IDS,
  DOMESTIC_PRODUCT_SOURCE_COUNT,
} from 'src/constants/product-sources';

/** 보드 종류 — 정본은 `src/constants/product-search-options` */
export const KIND_OPTIONS = PRODUCT_KIND_OPTIONS;

export const SOURCE_LABEL: Record<string, string> = PRODUCT_SOURCE_LABELS;

/**
 * 검색 전 안내 카드의 예시 칩 — 누르면 그대로 검색한다.
 *
 * ⚠️ 계측에서 **유입 경로가 갈린다.** 이 칩으로 시작한 검색은 `via: 'example'` 이다
 * (직접 입력은 `input`, `?q=` 링크는 `link`).
 */
export const SEARCH_EXAMPLES = ['마데카소사이드', '두피 앰플', '수분 크림', '시카 진정', '레티놀', '무기자차 선크림'];

/** 검색 결과 정렬 — 정본은 `src/constants/product-search-options` */
export const SEARCH_SORT_OPTIONS = PRODUCT_SEARCH_SORT_OPTIONS;

/** 보드에 채널을 세우는 순서 — 정본은 `src/constants/product-sources` */
export const SOURCE_ORDER = PRODUCT_SOURCE_ORDER;

export const SOURCE_COLOR: Record<string, string> = {
  oliveyoung: '#00A65E',
  hwahae: '#5B5BD6',
  musinsa: '#212B36',
  kakaogift: '#F7B32B',
  amazon: '#FF9900',
};

/** 해외 판매채널 — 보드를 국내/북미로 가르는 기준. 정본은 `src/constants/product-sources` */
export const OVERSEAS_SOURCE_IDS = OVERSEAS_PRODUCT_SOURCE_IDS;

/** 국내 판매몰 수 — 정본은 `src/constants/product-sources`(`DOMESTIC_PRODUCT_SOURCE_COUNT`). 다른 화면은 정본을 집는다 */
export const DOMESTIC_SOURCE_COUNT = DOMESTIC_PRODUCT_SOURCE_COUNT;

/** 지역 이름 — 값은 경계 어휘(`domestic`·`overseas`) 그대로다 */
export const REGION_LABEL: Record<string, string> = {
  domestic: '국내',
  overseas: '글로벌',
};

/** 검색 결과의 「판매 지역」 칩 — 정본은 `src/constants/product-search-options` */
export const REGION_OPTIONS = PRODUCT_REGION_OPTIONS;

/** 타 채널 매칭 확신도 — `product-rankings-match`의 `matchConfidence`가 정하는 값 */
export const MATCH_LABEL: Record<string, string> = {
  exact: '동일 상품',
  strong: '동일 상품(표기 차이)',
  similar: '유사 상품',
};

export const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 가격대 칩 — **경계는 `constants/product-price-bands` 한 벌이 쥐고, 이름만 화면이 짓는다.**
 *
 * 표시가가 색인에 없어 절은 서버가 만들어야 하고(할인가 유무로 갈린다) 사람이 읽는 이름은
 * 화면 몫이라, 공유하는 것은 경계 숫자뿐이다.
 *
 * `test`는 **판매몰 실시간 결과**를 같은 구간으로 거르고 세는 데 쓴다 — 그쪽은 서버를
 * 안 거치므로 화면이 직접 판정해야 한다.
 *
 * ⚠️ **원화 기준이라 해외몰(아마존·USD)은 구간을 고르면 빠진다** — 환산하지 않는다.
 * 서버 절도 같은 판정을 한다(`buildProductPriceBandClause` 의 통화 가드).
 */
export const PRICE_BAND_OPTIONS: {
  value: string;
  label: string;
  test: (price: number) => boolean;
}[] = PRODUCT_PRICE_BAND_SPECS.map((spec) => ({
  value: spec.value,
  label: formatProductPriceBandLabel(spec),
  test: (price: number) => isPriceInBand(spec, price),
}));

/**
 * 상세페이지가 **어떻게** 바뀌었나 — 버전 문서의 `changeType`.
 *
 * ⚠️ 컷을 지문(pHash)으로 관리해서 "무엇이 몇 개 붙고 빠졌는지"까지 안다. 브랜드가 개편
 * 때 무엇을 덧붙였는지가 이 화면의 관심사라 종류를 배지에 싣는다.
 *
 * ⚠️ `none`·`new`는 **배지가 뜨는 상황이 아니다**(변경이 없거나 첫 수집이다). 그래서
 * 라벨은 있어도 배지에는 일반 문구(「개편」)로 나간다.
 */
export const CHANGE_TYPE_LABEL: Record<string, string> = {
  none: '변경 없음',
  'text-only': '문구만 변경',
  append: '컷 추가',
  trim: '컷 삭제',
  swap: '컷 교체',
  rebuild: '전면 개편',
  new: '첫 수집',
};

export const COLLECT_STATUS_LABEL: Record<string, string> = {
  start: '상세페이지를 여는 중',
  collected: '수집 완료',
  reused: '이미 수집된 내용 재사용',
  nomedia: '상세 이미지가 없는 상품',
  skip: '건너뜀',
  error: '수집 실패',
};

/**
 * ⚠️ `eventInfo`는 **판매처마다 성격이 다르다.**
 *
 * ```
 *   올리브영  1+1 · 오특 · 베스트
 *   무신사    급상승 · 판매 N개
 *   카카오    단독 · ○○ 증정
 * ```
 *
 * 그래서 출처가 아니라 **문구를 보고** 색을 고른다. 순서가 곧 우선순위다.
 */
export const EVENT_TONES = [
  { re: /1\s*\+\s*1|2\s*\+\s*1|증정|사은품/, color: 'error.dark', bg: 'error.lighter' },
  { re: /오특|단독|한정|특가|세일/, color: 'primary.dark', bg: 'primary.lighter' },
  { re: /베스트|급상승|인기|1위/, color: 'warning.dark', bg: 'warning.lighter' },
];
